"use client";

/**
 * Hooks for managing playbooks, executions, and node types.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  playbooksListPlaybooks,
  playbooksCreatePlaybook,
  playbooksGetPlaybook,
  playbooksUpdatePlaybook,
  playbooksDeletePlaybook,
  playbooksActivatePlaybook,
  playbooksPausePlaybook,
  playbooksRollbackPlaybook,
  playbooksExecutePlaybook,
  playbooksListExecutions,
  playbooksGetExecution,
  playbooksCancelExecution,
  playbooksGetNodeTypes,
  playbooksListReports,
  playbooksGetMonitorOverview,
  playbooksGetScheduleTimeline,
  playbooksListAllReports,
  playbooksListAllEmails,
  playbooksListAllSlackMessages,
} from "../sdk";
import type {
  SrcPlaybooksResponseModelsPlaybookSummary,
  PlaybookDetail,
  PlaybookType,
  PlaybookStatus,
  PlaybookNodeType,
  ExecutionStatus,
  ExecutionSummary,
  ExecutionDetail,
  NodeExecutionDetail,
  NodeTypeSchema,
  NarrativeStep,
  NarrativeBranch,
  ReportSummary,
  CreatePlaybookRequest,
  UpdatePlaybookRequest,
  MonitorOverviewResponse,
  MonitorKpis,
  WeeklyOutputSummary,
  RunningExecutionSummary,
  ScheduledExecution,
  RecentExecutionSummary,
  ScheduleTimelineResponse,
  CrossPlaybookReportSummary,
  CrossPlaybookReportListResponse,
  EmailOutputSummary,
  EmailOutputListResponse,
  SlackOutputSummary,
  SlackOutputListResponse,
} from "../sdk";

// Alias for cleaner usage
export type PlaybookSummary = SrcPlaybooksResponseModelsPlaybookSummary;

// Re-export SDK types for consumers
export type {
  PlaybookDetail,
  PlaybookType,
  PlaybookStatus,
  PlaybookNodeType,
  ExecutionStatus,
  ExecutionSummary,
  ExecutionDetail,
  NodeExecutionDetail,
  NodeTypeSchema,
  NarrativeStep,
  NarrativeBranch,
  ReportSummary,
  MonitorOverviewResponse,
  MonitorKpis,
  WeeklyOutputSummary,
  RunningExecutionSummary,
  ScheduledExecution,
  RecentExecutionSummary,
  ScheduleTimelineResponse,
  CrossPlaybookReportSummary,
  CrossPlaybookReportListResponse,
  EmailOutputSummary,
  EmailOutputListResponse,
  SlackOutputSummary,
  SlackOutputListResponse,
};

// Custom types for playbook nodes and edges
export type NodeCategory = "triggers" | "ai" | "logic" | "code" | "integrations" | "data" | "documents" | "wayak";

export interface PlaybookNode {
  id: string;
  type: PlaybookNodeType;
  position: {
    x: number;
    y: number;
  };
  data: {
    name?: string;
    description?: string;
    config: Record<string, unknown>;
  };
}

export interface PlaybookEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

// =============================================================================
// usePlaybooks - List playbooks with filtering
// =============================================================================

interface UsePlaybooksOptions {
  orgId: string;
  type?: PlaybookType;
  status?: PlaybookStatus;
}

interface UsePlaybooksResult {
  playbooks: PlaybookSummary[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePlaybooks(options: UsePlaybooksOptions): UsePlaybooksResult {
  const { orgId, type, status } = options;

  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlaybooks = useCallback(async () => {
    if (!orgId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksListPlaybooks({
        query: {
          org_id: orgId,
          playbook_type: type,
          status: status,
        },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch playbooks"
        );
      }

      setPlaybooks(response.data?.playbooks || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, type, status]);

  useEffect(() => {
    fetchPlaybooks();
  }, [fetchPlaybooks]);

  return {
    playbooks,
    total,
    isLoading,
    error,
    refetch: fetchPlaybooks,
  };
}

// =============================================================================
// usePlaybook - Single playbook management with CRUD operations
// =============================================================================

interface UsePlaybookOptions {
  orgId: string;
  playbookId?: string;
  enabled?: boolean;
}

interface UsePlaybookResult {
  playbook: PlaybookDetail | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updatePlaybook: (updates: {
    name?: string;
    description?: string;
    icon?: string;
    cron_expression?: string;
    timezone?: string;
    nodes?: PlaybookNode[];
    edges?: PlaybookEdge[];
    variables?: Record<string, unknown>;
  }) => Promise<PlaybookDetail>;
  activate: () => Promise<PlaybookDetail>;
  pause: () => Promise<PlaybookDetail>;
  rollback: () => Promise<PlaybookDetail>;
  deletePlaybook: () => Promise<void>;
  execute: () => Promise<ExecutionSummary>;
}

export function usePlaybook(options: UsePlaybookOptions): UsePlaybookResult {
  const { orgId, playbookId, enabled = true } = options;

  const [playbook, setPlaybook] = useState<PlaybookDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlaybook = useCallback(async () => {
    if (!orgId || !playbookId || !enabled) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksGetPlaybook({
        path: { playbook_id: playbookId },
        query: { org_id: orgId },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch playbook"
        );
      }

      setPlaybook(response.data || null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, playbookId, enabled]);

  useEffect(() => {
    fetchPlaybook();
  }, [fetchPlaybook]);

  const updatePlaybook = useCallback(
    async (updates: {
      name?: string;
      description?: string;
      icon?: string;
      cron_expression?: string;
      timezone?: string;
      nodes?: PlaybookNode[];
      edges?: PlaybookEdge[];
      variables?: Record<string, unknown>;
    }): Promise<PlaybookDetail> => {
      if (!orgId || !playbookId) throw new Error("No org ID or playbook ID provided");

      const response = await playbooksUpdatePlaybook({
        path: { playbook_id: playbookId },
        query: { org_id: orgId },
        body: updates as unknown as UpdatePlaybookRequest,
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to update playbook"
        );
      }

      const updated = response.data!;
      setPlaybook(updated);
      return updated;
    },
    [orgId, playbookId]
  );

  const activate = useCallback(async (): Promise<PlaybookDetail> => {
    if (!orgId || !playbookId) throw new Error("No org ID or playbook ID provided");

    const response = await playbooksActivatePlaybook({
      path: { playbook_id: playbookId },
      query: { org_id: orgId },
    });

    if (response.error) {
      throw new Error(
        (response.error as { detail?: string }).detail || "Failed to activate playbook"
      );
    }

    const updated = response.data!;
    setPlaybook(updated);
    return updated;
  }, [orgId, playbookId]);

  const pause = useCallback(async (): Promise<PlaybookDetail> => {
    if (!orgId || !playbookId) throw new Error("No org ID or playbook ID provided");

    const response = await playbooksPausePlaybook({
      path: { playbook_id: playbookId },
      query: { org_id: orgId },
    });

    if (response.error) {
      throw new Error(
        (response.error as { detail?: string }).detail || "Failed to pause playbook"
      );
    }

    const updated = response.data!;
    setPlaybook(updated);
    return updated;
  }, [orgId, playbookId]);

  const rollback = useCallback(async (): Promise<PlaybookDetail> => {
    if (!orgId || !playbookId) throw new Error("No org ID or playbook ID provided");

    const response = await playbooksRollbackPlaybook({
      path: { playbook_id: playbookId },
      query: { org_id: orgId },
    });

    if (response.error) {
      throw new Error(
        (response.error as { detail?: string }).detail || "Failed to rollback playbook"
      );
    }

    const updated = response.data!;
    setPlaybook(updated);
    return updated;
  }, [orgId, playbookId]);

  const deletePlaybook = useCallback(async (): Promise<void> => {
    if (!orgId || !playbookId) throw new Error("No org ID or playbook ID provided");

    const response = await playbooksDeletePlaybook({
      path: { playbook_id: playbookId },
      query: { org_id: orgId },
    });

    if (response.error) {
      throw new Error(
        (response.error as { detail?: string }).detail || "Failed to delete playbook"
      );
    }

    setPlaybook(null);
  }, [orgId, playbookId]);

  const execute = useCallback(async (): Promise<ExecutionSummary> => {
    if (!orgId || !playbookId) throw new Error("No org ID or playbook ID provided");

    const response = await playbooksExecutePlaybook({
      path: { playbook_id: playbookId },
      query: { org_id: orgId },
      body: {},
    });

    if (response.error) {
      throw new Error(
        (response.error as { detail?: string }).detail || "Failed to execute playbook"
      );
    }

    return response.data!;
  }, [orgId, playbookId]);

  return {
    playbook,
    isLoading,
    error,
    refetch: fetchPlaybook,
    updatePlaybook,
    activate,
    pause,
    rollback,
    deletePlaybook,
    execute,
  };
}

// =============================================================================
// useCreatePlaybook - Create new playbooks
// =============================================================================

interface CreatePlaybookInput {
  org_id: string;
  name: string;
  description?: string;
  icon?: string;
  playbook_type: PlaybookType;
  cron_expression?: string;
  timezone?: string;
  nodes?: PlaybookNode[];
  edges?: PlaybookEdge[];
  variables?: Record<string, unknown>;
}

interface UseCreatePlaybookResult {
  createPlaybook: (input: CreatePlaybookInput) => Promise<PlaybookDetail>;
  isLoading: boolean;
  error: string | null;
}

export function useCreatePlaybook(): UseCreatePlaybookResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPlaybook = useCallback(
    async (input: CreatePlaybookInput): Promise<PlaybookDetail> => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await playbooksCreatePlaybook({
          body: input as unknown as CreatePlaybookRequest,
          query: { org_id: input.org_id },
        });

        if (response.error) {
          throw new Error(
            (response.error as { detail?: string }).detail || "Failed to create playbook"
          );
        }

        return response.data!;
      } catch (err) {
        const errorMessage = (err as Error).message;
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    createPlaybook,
    isLoading,
    error,
  };
}

// =============================================================================
// usePlaybookExecutions - List executions for a playbook
// =============================================================================

interface UsePlaybookExecutionsOptions {
  orgId: string;
  playbookId: string;
  limit?: number;
  offset?: number;
}

interface UsePlaybookExecutionsResult {
  executions: ExecutionSummary[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePlaybookExecutions(
  options: UsePlaybookExecutionsOptions
): UsePlaybookExecutionsResult {
  const { orgId, playbookId, limit = 20, offset = 0 } = options;

  const [executions, setExecutions] = useState<ExecutionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExecutions = useCallback(async () => {
    if (!orgId || !playbookId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksListExecutions({
        path: { playbook_id: playbookId },
        query: { org_id: orgId, limit, offset },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch executions"
        );
      }

      setExecutions(response.data?.executions || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, playbookId, limit, offset]);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  return {
    executions,
    total,
    isLoading,
    error,
    refetch: fetchExecutions,
  };
}

// =============================================================================
// usePlaybookExecution - Single execution with polling
// =============================================================================

interface UsePlaybookExecutionOptions {
  orgId: string;
  playbookId: string;
  executionId: string | null;
  pollInterval?: number;
  enabled?: boolean;
}

interface UsePlaybookExecutionResult {
  execution: ExecutionDetail | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  cancel: () => Promise<void>;
  isRunning: boolean;
  isComplete: boolean;
  isFailed: boolean;
}

export function usePlaybookExecution(
  options: UsePlaybookExecutionOptions
): UsePlaybookExecutionResult {
  const { orgId, playbookId, executionId, pollInterval = 30000, enabled = true } = options;

  const [execution, setExecution] = useState<ExecutionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);

  const fetchExecution = useCallback(async () => {
    if (!orgId || !playbookId || !executionId || !enabled) return;

    try {
      setIsLoading(true);

      const response = await playbooksGetExecution({
        path: { playbook_id: playbookId, execution_id: executionId },
        query: { org_id: orgId },
      });

      if (!isMounted.current) return;

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch execution"
        );
      }

      setExecution(response.data || null);
      setError(null);
    } catch (err) {
      if (isMounted.current) {
        setError((err as Error).message);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [orgId, playbookId, executionId, enabled]);

  const cancel = useCallback(async () => {
    if (!orgId || !playbookId || !executionId) {
      throw new Error("No org, playbook or execution ID provided");
    }

    const response = await playbooksCancelExecution({
      path: { playbook_id: playbookId, execution_id: executionId },
      query: { org_id: orgId },
    });

    if (response.error) {
      throw new Error(
        (response.error as { detail?: string }).detail || "Failed to cancel execution"
      );
    }

    // Refetch to get updated status
    await fetchExecution();
  }, [orgId, playbookId, executionId, fetchExecution]);

  // Polling for running executions
  useEffect(() => {
    isMounted.current = true;

    if (!orgId || !playbookId || !executionId || !enabled) return;

    fetchExecution();

    // Only poll if execution is in a running state
    const isTerminal =
      execution?.status === "success" ||
      execution?.status === "error" ||
      execution?.status === "cancelled";

    if (isTerminal) return;

    const interval = setInterval(() => {
      fetchExecution();
    }, pollInterval);

    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [orgId, playbookId, executionId, enabled, pollInterval, execution?.status, fetchExecution]);

  return {
    execution,
    isLoading,
    error,
    refetch: fetchExecution,
    cancel,
    isRunning: execution?.status === "running" || execution?.status === "pending",
    isComplete: execution?.status === "success",
    isFailed: execution?.status === "error",
  };
}

// =============================================================================
// useNodeTypes - Get available node types for playbook builder
// =============================================================================

interface UseNodeTypesOptions {
  orgId: string;
}

interface UseNodeTypesResult {
  nodeTypes: NodeTypeSchema[];
  nodeTypesByCategory: Record<NodeCategory, NodeTypeSchema[]>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useNodeTypes(options: UseNodeTypesOptions): UseNodeTypesResult {
  const { orgId } = options;

  const [nodeTypes, setNodeTypes] = useState<NodeTypeSchema[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNodeTypes = useCallback(async () => {
    if (!orgId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksGetNodeTypes({
        query: { org_id: orgId },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch node types"
        );
      }

      setNodeTypes(response.data || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchNodeTypes();
  }, [fetchNodeTypes]);

  // Group node types by category
  const nodeTypesByCategory = nodeTypes.reduce(
    (acc, nodeType) => {
      const category = (nodeType.category as NodeCategory) || "integrations";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(nodeType);
      return acc;
    },
    {
      triggers: [],
      ai: [],
      logic: [],
      code: [],
      integrations: [],
      data: [],
      documents: [],
      wayak: [],
    } as Record<NodeCategory, NodeTypeSchema[]>
  );

  return {
    nodeTypes,
    nodeTypesByCategory,
    isLoading,
    error,
    refetch: fetchNodeTypes,
  };
}

// =============================================================================
// usePlaybookReports - List PDF reports generated by a playbook
// =============================================================================

interface UsePlaybookReportsOptions {
  orgId: string;
  playbookId: string;
  limit?: number;
  offset?: number;
}

interface UsePlaybookReportsResult {
  reports: ReportSummary[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePlaybookReports(
  options: UsePlaybookReportsOptions
): UsePlaybookReportsResult {
  const { orgId, playbookId, limit = 20, offset = 0 } = options;

  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    if (!orgId || !playbookId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksListReports({
        path: { playbook_id: playbookId },
        query: { org_id: orgId, limit, offset },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch reports"
        );
      }

      setReports(response.data?.reports || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, playbookId, limit, offset]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return {
    reports,
    total,
    isLoading,
    error,
    refetch: fetchReports,
  };
}

// =============================================================================
// Monitor Dashboard Hooks
// =============================================================================

// useMonitorOverview - Get aggregated monitor dashboard data
interface UseMonitorOverviewOptions {
  orgId: string;
  pollInterval?: number; // Interval in ms for polling running executions
}

interface UseMonitorOverviewResult {
  data: MonitorOverviewResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  lastUpdated: Date | null;
}

export function useMonitorOverview(
  options: UseMonitorOverviewOptions
): UseMonitorOverviewResult {
  const { orgId, pollInterval = 30000 } = options;

  const [data, setData] = useState<MonitorOverviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchOverview = useCallback(async () => {
    if (!orgId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksGetMonitorOverview({
        query: { org_id: orgId },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch monitor overview"
        );
      }

      setData(response.data || null);
      setLastUpdated(new Date());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  // Effect 1: Initial fetch on mount or when fetchOverview changes
  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Effect 2: Conditional polling - only poll when there are running executions
  // Use length instead of array reference to avoid infinite re-renders
  const hasRunningExecutions = data?.running_executions?.length ?? 0;

  useEffect(() => {
    if (pollInterval <= 0 || hasRunningExecutions === 0) {
      return;
    }

    pollRef.current = setInterval(() => {
      fetchOverview();
    }, pollInterval);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [pollInterval, fetchOverview, hasRunningExecutions]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchOverview,
    lastUpdated,
  };
}

// useScheduleTimeline - Get scheduled executions for a date range
interface UseScheduleTimelineOptions {
  orgId: string;
  startDate: Date;
  endDate: Date;
}

interface UseScheduleTimelineResult {
  scheduledExecutions: ScheduledExecution[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useScheduleTimeline(
  options: UseScheduleTimelineOptions
): UseScheduleTimelineResult {
  const { orgId, startDate, endDate } = options;

  const [scheduledExecutions, setScheduledExecutions] = useState<ScheduledExecution[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeline = useCallback(async () => {
    if (!orgId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksGetScheduleTimeline({
        query: {
          org_id: orgId,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
        },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch schedule timeline"
        );
      }

      setScheduledExecutions(response.data?.scheduled_executions || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, startDate, endDate]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  return {
    scheduledExecutions,
    isLoading,
    error,
    refetch: fetchTimeline,
  };
}

// useAllReports - Get all reports across playbooks
interface UseAllReportsOptions {
  orgId: string;
  limit?: number;
  offset?: number;
  startDate?: Date;
  endDate?: Date;
}

interface UseAllReportsResult {
  reports: CrossPlaybookReportSummary[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAllReports(options: UseAllReportsOptions): UseAllReportsResult {
  const { orgId, limit = 50, offset = 0, startDate, endDate } = options;

  const [reports, setReports] = useState<CrossPlaybookReportSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    if (!orgId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksListAllReports({
        query: {
          org_id: orgId,
          limit,
          offset,
          start_date: startDate?.toISOString(),
          end_date: endDate?.toISOString(),
        },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch reports"
        );
      }

      setReports(response.data?.reports || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, limit, offset, startDate, endDate]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return {
    reports,
    total,
    isLoading,
    error,
    refetch: fetchReports,
  };
}

// useAllEmails - Get all emails across playbooks
interface UseAllEmailsOptions {
  orgId: string;
  limit?: number;
  offset?: number;
  startDate?: Date;
  endDate?: Date;
}

interface UseAllEmailsResult {
  emails: EmailOutputSummary[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAllEmails(options: UseAllEmailsOptions): UseAllEmailsResult {
  const { orgId, limit = 50, offset = 0, startDate, endDate } = options;

  const [emails, setEmails] = useState<EmailOutputSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmails = useCallback(async () => {
    if (!orgId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksListAllEmails({
        query: {
          org_id: orgId,
          limit,
          offset,
          start_date: startDate?.toISOString(),
          end_date: endDate?.toISOString(),
        },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch emails"
        );
      }

      setEmails(response.data?.emails || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, limit, offset, startDate, endDate]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  return {
    emails,
    total,
    isLoading,
    error,
    refetch: fetchEmails,
  };
}

// useAllSlackMessages - Get all Slack messages across playbooks
interface UseAllSlackMessagesOptions {
  orgId: string;
  limit?: number;
  offset?: number;
  startDate?: Date;
  endDate?: Date;
}

interface UseAllSlackMessagesResult {
  messages: SlackOutputSummary[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAllSlackMessages(
  options: UseAllSlackMessagesOptions
): UseAllSlackMessagesResult {
  const { orgId, limit = 50, offset = 0, startDate, endDate } = options;

  const [messages, setMessages] = useState<SlackOutputSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!orgId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await playbooksListAllSlackMessages({
        query: {
          org_id: orgId,
          limit,
          offset,
          start_date: startDate?.toISOString(),
          end_date: endDate?.toISOString(),
        },
      });

      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail || "Failed to fetch Slack messages"
        );
      }

      setMessages(response.data?.messages || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, limit, offset, startDate, endDate]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    messages,
    total,
    isLoading,
    error,
    refetch: fetchMessages,
  };
}
